import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useConfirm } from "../contexts/ConfirmContext";
import { formatDateWithYear, getTodayDate } from "../utils/common";

// ============================================================================
// HELPERS
// ============================================================================
const clampPercent = (current, target) => {
  if (target === 0) return 0;
  const percent = (current / target) * 100;
  return Math.min(Math.max(percent, 0), 100);
};

// ============================================================================
// COMPONENTE: GoalCard
// ============================================================================
const GoalCard = ({ 
  goal, 
  currentAmount, 
  contributions,
  onAddContribution, 
  onEdit, 
  onDelete,
  onDeleteContribution,
  formatAmount
}) => {
  const [showContributions, setShowContributions] = useState(false);
  const progress = clampPercent(currentAmount, goal.targetAmount);
  const isCompleted = goal.status === "COMPLETED";
  const goalContributions = contributions.filter(c => c.goalId === goal.id);

  return (
    <div className={`rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden bg-white dark:bg-gray-800 border ${
      isCompleted ? "border-2 border-green-200 dark:border-green-700/60" : "border-gray-200 dark:border-gray-700/50"
    }`}>
      {/* Header compacto */}
      <div className={`bg-gradient-to-r ${
        isCompleted 
          ? "from-green-500 to-green-600" 
          : "from-blue-500 to-blue-600"
      } px-3 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-white truncate">{goal.name}</h3>
          {isCompleted && (
            <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
              ✓ Completada
            </span>
          )}
        </div>
        {goal.targetDate && (
          <span className="text-[10px] text-white/80 whitespace-nowrap ml-2">
            {formatDateWithYear(goal.targetDate)}
          </span>
        )}
      </div>

      {/* Contenido compacto */}
      <div className="p-3">
        {/* Progreso */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-600 dark:text-gray-400">Progreso</span>
            <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
              {progress.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                isCompleted 
                  ? "bg-gradient-to-r from-green-500 to-green-600" 
                  : "bg-gradient-to-r from-blue-500 to-blue-600"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {formatAmount(currentAmount, undefined, goal.currency || "PEN")}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              de {formatAmount(goal.targetAmount, undefined, goal.currency || "PEN")}
            </p>
          </div>
        </div>

        {/* Botones de acción compactos */}
        <div className="flex gap-1.5 mb-3">
          <button
            onClick={() => onAddContribution(goal)}
            disabled={isCompleted}
            className="flex-1 px-2 py-1 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Agregar aporte"
          >
            + Aporte
          </button>
          <button
            onClick={() => setShowContributions(!showContributions)}
            className="px-2 py-1 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-medium rounded transition-colors"
            aria-label={showContributions ? "Ocultar aportes" : "Ver aportes"}
          >
            {showContributions ? "Ocultar" : "Ver"} ({goalContributions.length})
          </button>
        </div>

        {/* Lista de aportes (expandible) */}
        {showContributions && (
          <div className="border-t border-gray-100 dark:border-gray-700 pt-2 mb-3">
            {goalContributions.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                No hay aportes aún
              </p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {goalContributions
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((contribution) => (
                    <div
                      key={contribution.id}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                            {formatAmount(contribution.amount, undefined, goal.currency || "PEN")}
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {formatDateWithYear(contribution.date)}
                          </span>
                        </div>
                        {contribution.note && (
                          <p className="text-[10px] text-gray-600 dark:text-gray-400 truncate">{contribution.note}</p>
                        )}
                      </div>
                      <button
                        onClick={() => onDeleteContribution(contribution.id, goal.id)}
                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                        aria-label="Eliminar aporte"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Botones editar/eliminar compactos */}
        <div className="flex gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => onEdit(goal)}
            className="flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium rounded transition-colors"
            aria-label="Editar meta"
          >
            Editar
          </button>
          <button
            onClick={() => onDelete(goal.id, goal.name)}
            className="flex-1 px-2 py-1 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded transition-colors"
            aria-label="Eliminar meta"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: GoalModal (Crear/Editar)
// ============================================================================
const GoalModal = ({ isOpen, onClose, onSave, editingGoal, enabledCurrencies = ["PEN", "USD"] }) => {
  const [formData, setFormData] = useState({
    name: "",
    targetAmount: "",
    currency: "PEN",
    targetDate: "",
    status: "ACTIVE"
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (editingGoal) {
      setFormData({
        name: editingGoal.name,
        targetAmount: editingGoal.targetAmount.toString(),
        currency: editingGoal.currency || "PEN",
        targetDate: editingGoal.targetDate || "",
        status: editingGoal.status
      });
    } else {
      setFormData({
        name: "",
        targetAmount: "",
        currency: "PEN",
        targetDate: "",
        status: "ACTIVE"
      });
    }
    setErrors({});
  }, [editingGoal, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (!formData.targetAmount) {
      newErrors.targetAmount = "El objetivo es obligatorio";
    } else if (parseFloat(formData.targetAmount) <= 0) {
      newErrors.targetAmount = "El objetivo debe ser mayor a 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      ...formData,
      targetAmount: parseFloat(formData.targetAmount),
      currency: formData.currency || "PEN"
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-2xl font-bold text-white">
              {editingGoal ? "Editar meta" : "Nueva meta de ahorro"}
            </h2>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition-colors" aria-label="Cerrar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nombre de la meta <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ej: Viaje, Emergencia, Auto..."
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                  errors.name ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                }`}
              />
              {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Objetivo y Moneda */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Objetivo <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold whitespace-nowrap">
                    {CURRENCY_OPTIONS.find(opt => opt.code === formData.currency)?.symbol || "S/"}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.targetAmount}
                    onChange={(e) => handleChange("targetAmount", e.target.value)}
                    placeholder="0.00"
                    className={`w-full pl-20 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                      errors.targetAmount ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                    }`}
                  />
                </div>
                {errors.targetAmount && <p className="mt-2 text-sm text-red-600">{errors.targetAmount}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Moneda <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.currency || "PEN"}
                    onChange={(e) => handleChange("currency", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                  >
                    {CURRENCY_OPTIONS.filter(opt => enabledCurrencies.includes(opt.code)).map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <svg 
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Fecha objetivo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha objetivo (opcional)
              </label>
              <input
                type="date"
                value={formData.targetDate}
                onChange={(e) => handleChange("targetDate", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
              />
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="ACTIVE">Activa</option>
                  <option value="COMPLETED">Completada</option>
                </select>
                <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <button type="button" onClick={onClose} className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button type="submit" className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: ContributionModal
// ============================================================================
const ContributionModal = ({ isOpen, onClose, onSave, goal }) => {
  const [formData, setFormData] = useState({
    amount: "",
    date: getTodayDate(),
    note: ""
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    setFormData({
      amount: "",
      date: getTodayDate(),
      note: ""
    });
    setErrors({});
  }, [isOpen]);
  
  const goalCurrency = goal?.currency || "PEN";
  const currencySymbol = CURRENCY_OPTIONS.find(opt => opt.code === goalCurrency)?.symbol || "S/";

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.amount) {
      newErrors.amount = "El monto es obligatorio";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "El monto debe ser mayor a 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
            <div>
              <h2 className="text-2xl font-bold text-white">Agregar aporte</h2>
              {goal && <p className="text-green-100 text-sm mt-1">Meta: {goal.name}</p>}
            </div>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition-colors" aria-label="Cerrar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Monto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Monto <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold whitespace-nowrap">{currencySymbol}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-20 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    errors.amount ? "border-red-300 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                  }`}
                  autoFocus
                />
              </div>
              {errors.amount && <p className="mt-2 text-sm text-red-600">{errors.amount}</p>}
              {goal && (
                <p className="mt-1 text-xs text-gray-500">
                  Meta en {CURRENCY_OPTIONS.find(opt => opt.code === goalCurrency)?.label || goalCurrency}
                </p>
              )}
            </div>

            {/* Fecha */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all"
              />
            </div>

            {/* Nota */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nota (opcional)</label>
              <textarea
                value={formData.note}
                onChange={(e) => handleChange("note", e.target.value)}
                placeholder="Ej: Ahorro mensual, Extra..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all resize-none"
              />
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <button type="button" onClick={onClose} className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button type="submit" className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar aporte
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: SavingsGoals
// ============================================================================
const SavingsGoals = () => {
  const navigate = useNavigate();
  const { state, dispatch, getGoalCurrentAmount, formatDisplayAmount } = useControlCash();
  
  // Helper para formatear montos con moneda específica
  const formatAmount = (amount, isCount = false, currency = "PEN") => {
    if (isCount) return amount.toString();
    return formatDisplayAmount(amount, undefined, currency);
  };
  const { confirm } = useConfirm();
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const goals = state.goals;
  const contributions = state.contributions;

  // Calcular monto actual por meta
  const getCurrentAmount = (goalId) => {
    return getGoalCurrentAmount(goalId);
  };

  // Verificar y auto-completar metas que ya alcanzaron el 100% pero siguen activas
  React.useEffect(() => {
    // Solo verificar metas activas que no están completadas
    const activeGoalsToCheck = goals.filter(g => g.status === "ACTIVE");
    
    activeGoalsToCheck.forEach(goal => {
      const currentAmount = getCurrentAmount(goal.id);
      if (currentAmount >= goal.targetAmount && goal.targetAmount > 0) {
        dispatch({
          type: "UPDATE_GOAL",
          payload: {
            id: goal.id,
            patch: { status: "COMPLETED" }
          }
        });
        console.log("🎉 Meta auto-completada (ya tenía 100%):", goal.id, goal.name);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals.length, contributions.length]); // Solo ejecutar cuando cambie el número de metas o aportes

  // Handlers para metas
  const handleOpenGoalModal = () => {
    setEditingGoal(null);
    setIsGoalModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setIsGoalModalOpen(true);
  };

  const handleSaveGoal = (goalData) => {
    const payload = {
      ...goalData,
      targetAmount: parseFloat(goalData.targetAmount),
      currency: goalData.currency || "PEN"
    };
    if (editingGoal) {
      dispatch({
        type: "UPDATE_GOAL",
        payload: {
          id: editingGoal.id,
          patch: payload
        }
      });
      console.log("✏️ Meta editada:", editingGoal.id);
    } else {
      dispatch({
        type: "CREATE_GOAL",
        payload
      });
      console.log("➕ Nueva meta creada");
    }
    setIsGoalModalOpen(false);
  };

  const handleDeleteGoal = async (goalId, name) => {
    const ok = await confirm({
      title: "Eliminar meta",
      message: `¿Estás seguro de eliminar la meta "${name}"?\n\nSe eliminarán también todos sus aportes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({
        type: "DELETE_GOAL",
        payload: goalId
      });
      console.log("🗑️ Meta eliminada:", goalId);
    }
  };

  // Handlers para aportes
  const handleOpenContributionModal = (goal) => {
    setSelectedGoal(goal);
    setIsContributionModalOpen(true);
  };

  const handleSaveContribution = (contributionData) => {
    const goalCurrency = selectedGoal?.currency || "PEN";
    dispatch({
      type: "ADD_CONTRIBUTION",
      payload: {
        ...contributionData,
        amount: parseFloat(contributionData.amount),
        currency: goalCurrency,
        goalId: selectedGoal.id
      }
    });
    console.log("💰 Nuevo aporte agregado");

    // Auto-completar meta si se alcanza el objetivo
    const newCurrentAmount = getCurrentAmount(selectedGoal.id) + parseFloat(contributionData.amount);
    if (newCurrentAmount >= selectedGoal.targetAmount && selectedGoal.status === "ACTIVE") {
      dispatch({
        type: "UPDATE_GOAL",
        payload: {
          id: selectedGoal.id,
          patch: { status: "COMPLETED" }
        }
      });
      console.log("🎉 Meta completada automáticamente:", selectedGoal.id);
    }

    setIsContributionModalOpen(false);
  };

  const handleDeleteContribution = async (contributionId, goalId) => {
    const ok = await confirm({
      title: "Eliminar aporte",
      message: "¿Estás seguro de eliminar este aporte?",
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({
        type: "DELETE_CONTRIBUTION",
        payload: contributionId
      });
      console.log("🗑️ Aporte eliminado:", contributionId);

      // Si la meta estaba completada, volver a activarla si ya no alcanza el objetivo
      const goal = goals.find(g => g.id === goalId);
      if (goal && goal.status === "COMPLETED") {
        const newCurrentAmount = contributions
          .filter(c => c.goalId === goalId && c.id !== contributionId)
          .reduce((sum, c) => sum + c.amount, 0);
        if (newCurrentAmount < goal.targetAmount) {
          dispatch({
            type: "UPDATE_GOAL",
            payload: {
              id: goalId,
              patch: { status: "ACTIVE" }
            }
          });
          console.log("🔄 Meta reactivada:", goalId);
        }
      }
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader 
          onBack={handleBack}
          title="Metas de ahorro"
          subtitle="Crea metas, registra aportes y sigue tu progreso."
        />

        {/* Resumen compacto de estado */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
              {goals.filter(g => g.status === "ACTIVE").length} {goals.filter(g => g.status === "ACTIVE").length === 1 ? "meta activa" : "metas activas"}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800/50">
            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">
              {goals.filter(g => g.status === "COMPLETED").length} {goals.filter(g => g.status === "COMPLETED").length === 1 ? "completada" : "completadas"}
            </span>
          </div>
        </div>

        {/* Totales por moneda - diseño destacado */}
        {(() => {
          const enabledCurrencies = state.enabledCurrencies || ["PEN", "USD"];
          const goalsByCurrency = {};
          goals.forEach(goal => {
            const currency = goal.currency || "PEN";
            if (!goalsByCurrency[currency]) {
              goalsByCurrency[currency] = { total: 0, goals: [] };
            }
            goalsByCurrency[currency].goals.push(goal);
            const goalContributions = contributions.filter(c => c.goalId === goal.id);
            goalsByCurrency[currency].total += goalContributions.reduce((sum, c) => sum + c.amount, 0);
          });

          return (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Total ahorrado
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enabledCurrencies.map((currencyCode) => {
                  const currencyData = goalsByCurrency[currencyCode] || { total: 0, goals: [] };
                  const total = currencyData.total;
                  const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currencyCode);
                  const currencyLabel = currencyInfo?.label || currencyCode;
                  const currencySymbol = currencyInfo?.symbol || currencyCode;
                  
                  // Colores diferentes por moneda
                  const currencyColors = {
                    PEN: "from-emerald-500 to-teal-600 border-emerald-400/20",
                    USD: "from-blue-500 to-indigo-600 border-blue-400/20",
                    COP: "from-yellow-500 to-orange-600 border-yellow-400/20",
                    ARS: "from-sky-500 to-cyan-600 border-sky-400/20",
                    CLP: "from-red-500 to-pink-600 border-red-400/20",
                    VEF: "from-purple-500 to-violet-600 border-purple-400/20"
                  };
                  
                  const gradientClass = currencyColors[currencyCode] || "from-gray-500 to-gray-600 border-gray-400/20";
                  
                  return (
                    <div
                      key={currencyCode}
                      className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-xl font-bold text-white">
                              {currencySymbol}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-white/90 uppercase tracking-wide">
                            {currencyLabel}
                          </p>
                        </div>
                      </div>
                      <p className="text-3xl md:text-4xl font-bold text-white mb-1">
                        {formatDisplayAmount(total, undefined, currencyCode)}
                      </p>
                      <p className="text-xs text-white/70">
                        {currencyData.goals.length} {currencyData.goals.length === 1 ? "meta" : "metas"} en {currencyCode}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Botón principal */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mis metas</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {goals.length} {goals.length === 1 ? "meta registrada" : "metas registradas"}
            </p>
          </div>
          <button
            onClick={handleOpenGoalModal}
            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center gap-2"
            aria-label="Crear nueva meta"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Nueva meta</span>
            <span className="sm:hidden">Nueva</span>
          </button>
        </div>

        {/* Lista de metas */}
        {goals.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No hay metas aún</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primera meta de ahorro</p>
            <button
              onClick={handleOpenGoalModal}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Crear primera meta
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                currentAmount={getCurrentAmount(goal.id)}
                contributions={contributions}
                onAddContribution={handleOpenContributionModal}
                onEdit={handleEditGoal}
                onDelete={handleDeleteGoal}
                onDeleteContribution={handleDeleteContribution}
                formatAmount={formatAmount}
              />
            ))}
          </div>
        )}

        {/* Modales */}
        <GoalModal
          isOpen={isGoalModalOpen}
          onClose={() => setIsGoalModalOpen(false)}
          onSave={handleSaveGoal}
          editingGoal={editingGoal}
          enabledCurrencies={state.enabledCurrencies || ["PEN", "USD"]}
        />
        <ContributionModal
          isOpen={isContributionModalOpen}
          onClose={() => setIsContributionModalOpen(false)}
          onSave={handleSaveContribution}
          goal={selectedGoal}
        />
      </div>
    </Layout>
  );
};

export default SavingsGoals;
